// Runtime values come from window.__RUNTIME_CONFIG__, set by the non-bundled
// public/environment.js (rewritten per-env at container start by
// process-sentinel's reactEnv injection, configured in docker/sentinel.json).
// Every value there is ALWAYS a string — env vars are strings, so "" is what an
// unset var looks like; null/undefined never appear.
//
// Fallback chain per var: runtime value -> build-time VITE_* -> host-based default.
// The chain MUST use `||`, never `??`: "" is falsy but not nullish, so `??` would
// accept the empty string as a real value and every URL below would silently
// become "". Keep each import.meta.env.VITE_* access LITERAL — Vite only inlines
// static property access, so a computed import.meta.env[key] would break the
// build-time fallback. Adding a runtime var: add a line to public/environment.js
// AND an explicit export below.
//
// Both halves of each chain use the SAME name (VITE_API_URL, VITE_ACCOUNT_API_URL,
// …), so a variable is spelled one way everywhere: ui/.env for dev, Doppler and
// reactEnvVars for the container. The runtime half is a plain object key that never
// passes through Vite — it just keeps the prefix so there is no second name to
// mismatch. A key missing from reactEnvVars in docker/sentinel.json is never
// injected and stays "", falling through to the build-time value below.
declare global {
  interface Window {
    __RUNTIME_CONFIG__?: Record<string, string | undefined>;
  }
}

const runtimeConfig = window.__RUNTIME_CONFIG__ || {};

// import.meta.env.DEV is true for `npm run dev` and false for every build, so the
// local case is correct no matter which host the dev server is reached on — the
// hostname checks alone miss 127.0.0.1, LAN IPs, and tunnels, and those would
// otherwise fall through to the production URLs below. The host checks still cover
// deployed non-prod environments that did not get a runtime-injected URL.
export const isDev: boolean =
  import.meta.env.DEV ||
  window.location.host.indexOf('dev-') !== -1 ||
  window.location.host.indexOf('stg-') !== -1 ||
  window.location.host.indexOf('staging-') !== -1 ||
  window.location.host.indexOf('localhost') !== -1;
export const TOOLBOX_API_URL = runtimeConfig.VITE_API_URL || import.meta.env.VITE_API_URL || '/api';
export const ACCOUNT_API_URL =
  runtimeConfig.VITE_ACCOUNT_API_URL ||
  import.meta.env.VITE_ACCOUNT_API_URL ||
  (isDev
    ? 'https://staging-services.backatyou.com/account'
    : 'https://services.backatyou.com/account');
export const LISTING_API_URL =
  runtimeConfig.VITE_LISTING_API_URL ||
  import.meta.env.VITE_LISTING_API_URL ||
  (isDev
    ? 'https://staging-services.backatyou.com/listings'
    : 'https://services.backatyou.com/listings');
// The AI service is called directly from the browser (no proxy), so a local dev
// server points at the service-ai dev port before the deployed staging/prod hosts.
export const AI_API_URL =
  runtimeConfig.VITE_AI_API_URL ||
  import.meta.env.VITE_AI_API_URL ||
  (import.meta.env.DEV
    ? 'http://localhost:30331/v1'
    : isDev
      ? 'https://staging-services.backatyou.com/ai/v1'
      : 'https://services.backatyou.com/ai/v1');
// Build-time ONLY — deliberately not in the runtime chain above. The version is a
// property of the image, not of the environment it runs in, so it is baked by the
// ui-build stage (ENV VITE_BUILD_VERSION=${BUILD_VERSION}) and must never be
// runtime-injectable: a Doppler value could then disagree with the running code.
// Keep it out of public/environment.js and out of reactEnvVars.
export const BUILD_VERSION = import.meta.env.VITE_BUILD_VERSION || '0.0.0-local';
